import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AsyncSelect, Button, FormCell, FormRow, FormSection, Multiselect } from '~/components';
import { STATES } from '~/constants';
import { SubspecialtyFilter } from '~/features/market/listings/components/SubspecialtyFields';
import { useResetSubspecialties } from '~/features/market/listings/hooks/useSubspecialties';
import { loadAOIOptionByProviderCategory } from '~/features/market/listings/msaas-listing/wizard-steps/basic-info/utils';
import { MarketContentPanel } from '~/features/shared/market-content';
import { loadCategoryOptions } from '~/features/vault/profile/api';
import type { CompareListingOption } from '../api';
import { isEditedFromListing, PREFILLED_KEYS, prefillFromListing } from '../compareListings';
import { type OfferingSearchValues } from '../offeringSearch';
import { useListingProviderCategories } from '../ProviderCategoriesContext';
import { useLocationOptions } from '../useLocationOptions';

// A module constant: useResetSubspecialties takes it as an effect dependency, so an inline object
// would re-create its watch subscription on every render.
const RESET_SUBSPECIALTIES_OPTIONS = { clearSpecialtyWithCategory: { empty: null } } as const;

/**
 * What the compare picker has filled in. The owner holds it, not the filters: on a narrow screen
 * they unmount each time the drawer closes, and this must outlive that (the "Edited from" note, and
 * a location lookup still in flight, which a newer choice has to be able to drop).
 */
export const useOfferingPrefill = () => {
  // What the compare listing filled in, to tell when the buyer has edited it since.
  const prefilled = useRef<Partial<OfferingSearchValues> | undefined>(undefined);
  // The newest compare choice (or clear). A location lookup that finishes for an older one is
  // dropped, so a slow listing A cannot overwrite the filters the buyer has since set from B.
  const latestCompare = useRef(0);
  const [prefilledFrom, setPrefilledFrom] = useState<string | undefined>(undefined);
  return { prefilled, latestCompare, prefilledFrom, setPrefilledFrom };
};

type Props = {
  // The buyer's open listings; the compare picker is hidden when there are none (DD10).
  compareListings: CompareListingOption[];
  prefill: ReturnType<typeof useOfferingPrefill>;
  onSearch: () => void;
  onClear: () => void;
};

export const OfferingSearchFilters = ({ compareListings, prefill, onSearch, onClear }: Props) => {
  const form = useFormContext<OfferingSearchValues>();
  const { setValue, watch } = form;
  useResetSubspecialties(form, RESET_SUBSPECIALTIES_OPTIONS);
  const lookup = useListingProviderCategories();
  const loadCategories = lookup?.loadCategories;
  const categoryOptions = lookup?.categories?.map((category) => ({
    value: category.id,
    label: category.providerCategoryCode,
  }));
  const loadSharedCategoryOptions = useCallback(
    async (input: string) => {
      if (!loadCategories) return loadCategoryOptions(input);
      const categories = await loadCategories();
      return categories
        .map((category) => ({ value: category.id, label: category.providerCategoryCode }))
        .filter((option) => option.label.toLowerCase().includes(input.toLowerCase()));
    },
    [loadCategories]
  );
  const { loadOptions: loadLocationOptions, noOptionsMessage } = useLocationOptions();

  const compareOptions = useMemo(
    () => compareListings.map((listing) => ({ value: listing.id, label: listing.listingTitle })),
    [compareListings]
  );
  const loadCompareOptions = useCallback(
    async (input: string) =>
      compareOptions.filter((option) => option.label.toLowerCase().includes(input.toLowerCase())),
    [compareOptions]
  );

  const { prefilled, latestCompare, prefilledFrom, setPrefilledFrom } = prefill;
  const providerCategory = watch('providerCategory');
  const hasSpecialty = !!watch('aoiSpecialty')?.value;
  const current = {
    location: watch('location'),
    states: watch('states'),
    providerCategory,
    aoiSpecialty: watch('aoiSpecialty'),
    subspecialties: watch('subspecialties'),
  };

  const handleCompareChange = async (option: unknown) => {
    const picked = option as { value: number; label: string } | null;
    const request = ++latestCompare.current;
    const listing = compareListings.find((item) => item.id === picked?.value);
    if (!picked || !listing) {
      prefilled.current = undefined;
      setPrefilledFrom(undefined);
      return;
    }
    const values = await prefillFromListing(listing, lookup?.categories);
    if (request !== latestCompare.current) return;
    PREFILLED_KEYS.forEach((field) =>
      // Only fills the form; the search itself still waits for the Search button.
      setValue(
        field,
        values[field] ?? (field === 'states' || field === 'subspecialties' ? [] : null)
      )
    );
    prefilled.current = values;
    setPrefilledFrom(picked.label);
  };

  const edited = isEditedFromListing(current, prefilled.current);

  return (
    <MarketContentPanel variant="compact">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="sub-heading-sb">Filters</h3>
        <button
          type="button"
          onClick={() => {
            latestCompare.current += 1;
            prefilled.current = undefined;
            setPrefilledFrom(undefined);
            onClear();
          }}
          className="text-sm font-medium text-primary-50 transition-colors hover:text-primary-t-300"
        >
          Clear All
        </button>
      </div>
      <FormSection componentsPerLine={3}>
        {compareListings.length > 0 && (
          <FormRow>
            <div>
              <AsyncSelect
                name="compareListing"
                cyTag="offering-search-compare"
                label="Compare to my listing"
                placeholder="Choose a listing"
                minChars={false}
                defaultOptions={compareOptions}
                cacheOptions={false}
                loadOptions={loadCompareOptions}
                onChange={(option) => void handleCompareChange(option)}
              />
              {prefilledFrom && edited && (
                <p className="text-sm leading-5 text-content-secondary" data-cy="offering-edited">
                  Edited from {prefilledFrom}
                </p>
              )}
            </div>
          </FormRow>
        )}
        <FormRow>
          <AsyncSelect
            name="location"
            cyTag="offering-search-location"
            label="Location"
            placeholder="City, ST or ZIP"
            noOptionsMessage={noOptionsMessage}
            loadingMessage="Searching..."
            minChars={false}
            defaultOptions={false}
            // A failed or empty lookup must not be remembered: the same query is tried again.
            cacheOptions={false}
            filterOption={null}
            loadOptions={loadLocationOptions}
          />
        </FormRow>
        <FormRow>
          <Multiselect
            name="states"
            cyTag="offering-search-states"
            label="Required license states"
            options={STATES}
            useShortValue
          />
        </FormRow>
        <FormRow>
          <AsyncSelect
            name="providerCategory"
            cyTag="offering-search-provider-category"
            label="Provider Category"
            minChars={false}
            defaultOptions={categoryOptions ?? true}
            loadOptions={loadSharedCategoryOptions}
          />
        </FormRow>
        <FormRow>
          <AsyncSelect
            name="aoiSpecialty"
            cyTag="offering-search-specialty"
            label="Provider Specialty"
            minChars={false}
            key={'aoiSpecialty-' + providerCategory?.value}
            loadOptions={(inputValue: string) =>
              providerCategory?.value
                ? loadAOIOptionByProviderCategory(providerCategory.value, inputValue)
                : Promise.resolve([])
            }
          />
        </FormRow>
        {hasSpecialty && (
          <FormRow>
            <SubspecialtyFilter cyTag="offering-search-subspecialties" />
          </FormRow>
        )}
        <FormCell span={12}>
          <div className="flex items-center justify-end gap-2 whitespace-nowrap">
            <Button label="Search" onClick={onSearch} variant="rounded" />
          </div>
        </FormCell>
      </FormSection>
    </MarketContentPanel>
  );
};
